import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { selectActiveOrganizationId } from "../../redux/authSlice";
import { useHistory } from "react-router-dom";
import {
  downloadBulkJobFile,
  downloadFailedReport,
  listBulkJobRecords,
  listBulkJobs,
  publishPartialBulkJob,
  renameBulkJobSet,
} from "../../services/bulkJobService";
import { loadTenantOrganizationsOnce } from "../../Shared/Organization/orgNameCache";
import {
  openErrorNotification,
  openSuccessNotification,
} from "../../helpers/methods";
import YhButton from "../../ui/YhButton";
import YhInput from "../../ui/YhInput";
import YhModal from "../../ui/YhModal";
import YhTable, { YhTableCellParams, YhTableColumn } from "../../ui/YhTable";
import BulkAssignModal from "../AllLeads/components/ShowAll/BulkAssignModal";
import urls from "../../global/constants/UrlConstants";
import {
  formatPhoneKeepingCountryCode,
  resolveLeadPhoneForDisplay,
} from "../../Shared/Phone/PhoneWithCountryCode";

type Props = {
  defaultType?: "leads" | "channel_partners";
};

const statusTone = (status: string) => {
  const s = String(status || "");
  if (s === "completed") return "bg-[#DCFCE7] text-[#15803D]";
  if (s === "partial") return "bg-[#FEF3C7] text-[#B45309]";
  if (s === "failed") return "bg-[#FEE2E2] text-[#B91C1C]";
  if (s === "processing") return "bg-[#DBEAFE] text-[#1D4ED8]";
  return "bg-[#F3F4F6] text-[#4B5563]";
};

const BulkUploadSetsPage: React.FC<Props> = ({ defaultType = "leads" }) => {
  const activeOrganizationId = useSelector(selectActiveOrganizationId) || "";
  const history = useHistory();
  const [jobs, setJobs] = useState<any[]>([]);
  const [orgs, setOrgs] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const [renameOpen, setRenameOpen] = useState(false);
  const [renameJob, setRenameJob] = useState<any | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [renaming, setRenaming] = useState(false);
  const [publishingId, setPublishingId] = useState<string>("");

  const [viewOpen, setViewOpen] = useState(false);
  const [viewJob, setViewJob] = useState<any | null>(null);
  const [viewRows, setViewRows] = useState<any[]>([]);
  const [viewMeta, setViewMeta] = useState<any>({});
  const [viewLoading, setViewLoading] = useState(false);
  const [viewSearch, setViewSearch] = useState("");
  const [viewPage, setViewPage] = useState(0);
  const [viewPageSize, setViewPageSize] = useState(25);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignLeadIds, setAssignLeadIds] = useState<string[]>([]);
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignJob, setAssignJob] = useState<any | null>(null);

  const createPath =
    defaultType === "channel_partners"
      ? urls.cpBulkUploadPath
      : urls.leadsBulkUploadPath;

  const jobSetLabel = (j: any) =>
    String(j?.setName || "").trim() ||
    String(j?.subSource || "").trim() ||
    String(j?.originalFileName || "").trim() ||
    (j?._id ? `Set ${String(j._id).slice(-6)}` : "Upload set");

  const loadJobs = useCallback(async () => {
    setLoading(true);
    try {
      const orgId =
        activeOrganizationId &&
        activeOrganizationId !== "all" &&
        activeOrganizationId !== "*"
          ? String(activeOrganizationId)
          : "";
      const res = await listBulkJobs({
        type: defaultType,
        limit: 50,
        // Only finalized uploads — dataset name is created on Save unique
        status: "completed,partial,failed,processing",
        ...(orgId ? { organizationId: orgId } : {}),
      });
      const items = Array.isArray(res?.data?.items)
        ? res.data.items
        : Array.isArray(res?.items)
        ? res.items
        : [];
      setJobs(items);
    } catch {
      setJobs([]);
    } finally {
      setLoading(false);
    }
  }, [defaultType, activeOrganizationId]);

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  useEffect(() => {
    (async () => {
      try {
        const res = await loadTenantOrganizationsOnce();
        const list = Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res?.data?.items)
          ? res.data.items
          : [];
        setOrgs(Array.isArray(list) ? list : []);
      } catch {
        setOrgs([]);
      }
    })();
  }, []);

  const orgLabelById = useMemo(() => {
    const map = new Map<string, string>();
    for (const o of orgs) {
      const id = String(o._id || o.id || "");
      if (!id) continue;
      map.set(
        id,
        o.name || o.brandName || o.legalName || o.slug || id
      );
    }
    return map;
  }, [orgs]);

  const openRename = (job: any) => {
    setRenameJob(job);
    setRenameValue(jobSetLabel(job));
    setRenameOpen(true);
  };

  const submitRename = async () => {
    const name = renameValue.trim();
    if (!renameJob?._id) return;
    if (!name) {
      openErrorNotification("Dataset name is required");
      return;
    }
    setRenaming(true);
    try {
      const res = await renameBulkJobSet(String(renameJob._id), name);
      if (res?.success === 0) {
        openErrorNotification(res?.message || "Rename failed");
        return;
      }
      openSuccessNotification("Dataset renamed");
      setRenameOpen(false);
      setRenameJob(null);
      loadJobs();
    } catch (err: any) {
      openErrorNotification(err?.message || "Rename failed");
    } finally {
      setRenaming(false);
    }
  };

  const publishSet = async (job: any) => {
    const id = String(job?._id || "");
    if (!id) return;
    setPublishingId(id);
    try {
      const res = await publishPartialBulkJob(id);
      if (res?.success === 0) {
        openErrorNotification(res?.message || "Publish failed");
        return;
      }
      openSuccessNotification(
        res?.message ||
          "Successful rows are now available in All Leads and WhatsApp bulk sets"
      );
      loadJobs();
    } catch (err: any) {
      openErrorNotification(err?.message || "Publish failed");
    } finally {
      setPublishingId("");
    }
  };

  const openInAllLeads = (job: any) => {
    const id = String(job?._id || "");
    if (!id) return;
    history.push(`${urls.ALLLEADFORMLIST}?uploadLeadId=${encodeURIComponent(id)}`);
  };

  const assignEntireSet = async (job: any) => {
    const id = String(job?._id || "");
    if (!id) return;
    if (defaultType !== "leads") {
      openErrorNotification("Assign is only available for lead datasets");
      return;
    }
    setAssignLoading(true);
    setAssignJob(job);
    try {
      const res = await listBulkJobRecords(id, { idsOnly: 1 });
      const ids = Array.isArray(res?.ids)
        ? res.ids.map(String)
        : (Array.isArray(res?.data) ? res.data : [])
            .map((r: any) => String(r?._id || ""))
            .filter(Boolean);
      if (!ids.length) {
        openErrorNotification("No leads found in this dataset to assign");
        return;
      }
      setAssignLeadIds(ids);
      setAssignOpen(true);
    } catch (err: any) {
      openErrorNotification(err?.message || "Failed to load leads for assign");
    } finally {
      setAssignLoading(false);
    }
  };

  const loadViewRecords = useCallback(
    async (jobId: string, page: number, pageSize: number, search: string) => {
      setViewLoading(true);
      try {
        const res = await listBulkJobRecords(jobId, {
          page,
          pageSize,
          search: search || undefined,
        });
        setViewRows(Array.isArray(res?.data) ? res.data : []);
        setViewMeta(res?.metadata?.[0] || {});
        if (res?.job) {
          setViewJob((prev: any) => ({ ...(prev || {}), ...res.job }));
        }
      } catch (err: any) {
        openErrorNotification(err?.message || "Failed to load dataset");
        setViewRows([]);
        setViewMeta({});
      } finally {
        setViewLoading(false);
      }
    },
    []
  );

  const openView = (job: any) => {
    setViewJob(job);
    setViewSearch("");
    setViewPage(0);
    setViewRows([]);
    setViewOpen(true);
  };

  useEffect(() => {
    if (!viewOpen || !viewJob?._id) return;
    loadViewRecords(
      String(viewJob._id),
      viewPage,
      viewPageSize,
      viewSearch
    );
  }, [
    viewOpen,
    viewJob?._id,
    viewPage,
    viewPageSize,
    viewSearch,
    loadViewRecords,
  ]);

  const viewColumns: YhTableColumn[] = useMemo(
    () => [
      {
        headerName: defaultType === "channel_partners" ? "Partner" : "Lead",
        field: "leadName",
        minWidth: 180,
        flex: 1,
        renderCell: (params: YhTableCellParams) => (
          <div className="flex min-w-0 flex-col">
            <span className="truncate text-sm font-semibold text-[#111827]">
              {params.row.leadName || params.row.firmName || "—"}
            </span>
            <span className="truncate text-xs text-[#6B7280]">
              {(() => {
                const phone = formatPhoneKeepingCountryCode(
                  resolveLeadPhoneForDisplay(params.row)
                );
                return phone !== "—"
                  ? phone
                  : params.row.leadEmail || params.row.email || "—";
              })()}
            </span>
          </div>
        ),
      },
      {
        headerName: "Source",
        field: "source",
        minWidth: 120,
        renderCell: (params: YhTableCellParams) => (
          <span className="text-sm text-[#374151]">
            {params.row.source || "—"}
          </span>
        ),
      },
      ...(defaultType === "leads"
        ? [
            {
              headerName: "Stage",
              field: "stage",
              minWidth: 120,
              renderCell: (params: YhTableCellParams) => (
                <span className="text-sm text-[#374151]">
                  {params.row.stage || "—"}
                </span>
              ),
            } as YhTableColumn,
          ]
        : []),
      {
        headerName: "Added",
        field: "createdAt",
        minWidth: 150,
        renderCell: (params: YhTableCellParams) => (
          <span className="text-xs text-[#6B7280]">
            {params.row.createdAt
              ? new Date(params.row.createdAt).toLocaleString()
              : "—"}
          </span>
        ),
      },
    ],
    [defaultType]
  );

  return (
    <div className="box-border w-full space-y-5 px-4 py-4 md:px-6 md:py-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight text-[#111827]">
            {defaultType === "channel_partners"
              ? "Partner upload sets"
              : "Bulk upload sets"}
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-[#6B7280]">
            Successful rows are saved even when status is partial. Click a
            dataset to view them, rename, or download failed rows to fix and
            re-upload.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <YhButton
            variant="secondary"
            size="sm"
            onClick={() => loadJobs()}
            disabled={loading}
          >
            Refresh
          </YhButton>
          <YhButton
            variant="primary"
            size="sm"
            onClick={() => history.push(createPath)}
          >
            New upload
          </YhButton>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-[#E5E7EB] bg-white shadow-sm">
        {!jobs.length ? (
          <div className="px-4 py-12 text-center">
            <p className="text-sm font-medium text-[#111827]">
              {loading ? "Loading sets…" : "No upload sets yet"}
            </p>
            {!loading ? (
              <p className="mt-1 text-xs text-[#6B7280]">
                Create an upload with a unique dataset name to see it here.
              </p>
            ) : null}
          </div>
        ) : (
          <div className="max-h-[calc(100vh-220px)] overflow-auto">
            <table className="min-w-full text-left text-xs">
              <thead className="sticky top-0 bg-[#F9FAFB] text-[#6B7280]">
                <tr>
                  <th className="px-3 py-2.5 font-medium">Dataset name</th>
                  <th className="px-3 py-2.5 font-medium">File</th>
                  <th className="px-3 py-2.5 font-medium">Organization</th>
                  <th className="px-3 py-2.5 font-medium">Status</th>
                  <th className="px-3 py-2.5 font-medium">Success</th>
                  <th className="px-3 py-2.5 font-medium">Failed</th>
                  <th className="px-3 py-2.5 font-medium">Date</th>
                  <th className="px-3 py-2.5 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((j) => {
                  const oid = String(
                    j.organizationId ||
                      (Array.isArray(j.organizationIds) &&
                        j.organizationIds[0]) ||
                      ""
                  );
                  const orgName =
                    orgLabelById.get(oid) ||
                    (oid ? `${oid.slice(0, 6)}…` : "—");
                  const label = jobSetLabel(j);
                  return (
                    <tr
                      key={String(j._id)}
                      className="cursor-pointer border-t border-[#F3F4F6] hover:bg-[#F8FAFC]"
                      onClick={() => openView(j)}
                    >
                      <td className="px-3 py-2.5">
                        <p className="font-semibold text-[#4338CA] hover:underline">
                          {label}
                        </p>
                        <p className="mt-0.5 text-[10px] text-[#9CA3AF]">
                          {j.subSource || "—"}
                        </p>
                      </td>
                      <td className="px-3 py-2.5">
                        <button
                          type="button"
                          className="max-w-[200px] truncate text-left font-medium text-[#4338CA] hover:underline"
                          title={j.originalFileName || "Download file"}
                          onClick={(e) => {
                            e.stopPropagation();
                            downloadBulkJobFile(
                              String(j._id),
                              j.originalFileName
                            ).catch((err) =>
                              openErrorNotification(
                                err?.message || "File not available"
                              )
                            );
                          }}
                        >
                          {j.originalFileName || "Download"}
                        </button>
                      </td>
                      <td className="px-3 py-2.5 text-[#374151]" title={oid}>
                        {orgName}
                      </td>
                      <td className="px-3 py-2.5">
                        <span
                          className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold capitalize ${statusTone(
                            j.status
                          )}`}
                        >
                          {j.status}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-[#065F46]">
                        {j.totals?.success ?? 0}
                      </td>
                      <td className="px-3 py-2.5 text-[#B45309]">
                        {j.totals?.fail ?? 0}
                      </td>
                      <td className="px-3 py-2.5 text-[#6B7280]">
                        {j.createdAt
                          ? new Date(j.createdAt).toLocaleString()
                          : "—"}
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="flex flex-wrap gap-3">
                          <button
                            type="button"
                            className="font-semibold text-[#111827] hover:underline"
                            onClick={(e) => {
                              e.stopPropagation();
                              openView(j);
                            }}
                          >
                            View
                          </button>
                          {defaultType === "leads" &&
                          Number(j.totals?.success || 0) > 0 ? (
                            <>
                              <button
                                type="button"
                                className="font-semibold text-[#0F766E] hover:underline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openInAllLeads(j);
                                }}
                              >
                                Filter in All Leads
                              </button>
                              <button
                                type="button"
                                className="font-semibold text-[#A02448] hover:underline disabled:opacity-50"
                                disabled={
                                  assignLoading &&
                                  String(assignJob?._id) === String(j._id)
                                }
                                onClick={(e) => {
                                  e.stopPropagation();
                                  assignEntireSet(j);
                                }}
                              >
                                {assignLoading &&
                                String(assignJob?._id) === String(j._id)
                                  ? "Loading…"
                                  : "Assign all"}
                              </button>
                            </>
                          ) : null}
                          <button
                            type="button"
                            className="font-semibold text-[#4338CA] hover:underline"
                            onClick={(e) => {
                              e.stopPropagation();
                              openRename(j);
                            }}
                          >
                            Rename
                          </button>
                          {["partial", "failed", "completed"].includes(
                            String(j.status)
                          ) && Number(j.totals?.success || 0) > 0 ? (
                            <button
                              type="button"
                              className="font-semibold text-[#15803D] hover:underline disabled:opacity-50"
                              disabled={publishingId === String(j._id)}
                              onClick={(e) => {
                                e.stopPropagation();
                                publishSet(j);
                              }}
                            >
                              {publishingId === String(j._id)
                                ? "Publishing…"
                                : j.marketingReady
                                ? "Re-publish"
                                : "Publish to leads"}
                            </button>
                          ) : null}
                          {["partial", "failed"].includes(String(j.status)) &&
                          Number(j.totals?.fail || 0) > 0 ? (
                            <button
                              type="button"
                              className="font-semibold text-[#B45309] hover:underline"
                              onClick={(e) => {
                                e.stopPropagation();
                                downloadFailedReport(String(j._id)).catch(
                                  (err) =>
                                    openErrorNotification(
                                      err?.message || "Failed report not available"
                                    )
                                );
                              }}
                            >
                              Failed.xlsx
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <YhModal
        open={renameOpen}
        onClose={() => {
          if (renaming) return;
          setRenameOpen(false);
          setRenameJob(null);
        }}
        title="Rename dataset"
        width={480}
        footer={
          <>
            <YhButton
              variant="secondary"
              size="sm"
              disabled={renaming}
              onClick={() => {
                setRenameOpen(false);
                setRenameJob(null);
              }}
            >
              Cancel
            </YhButton>
            <YhButton
              variant="primary"
              size="sm"
              disabled={renaming || !renameValue.trim()}
              onClick={submitRename}
            >
              {renaming ? "Saving…" : "Save name"}
            </YhButton>
          </>
        }
      >
        <YhInput
          label="Dataset name *"
          requiredMark
          value={renameValue}
          onChange={(e: any) => setRenameValue(e.target.value)}
          placeholder="Unique name for this organization"
          autoFocus
          onKeyPress={(e: any) => {
            if (e.key === "Enter") {
              e.preventDefault();
              submitRename();
            }
          }}
        />
        <p className="mt-2 text-xs text-[#6B7280]">
          Name must be unique for this organization. Used in WhatsApp audience
          filters.
        </p>
      </YhModal>

      <YhModal
        open={viewOpen}
        onClose={() => {
          setViewOpen(false);
          setViewJob(null);
          setViewRows([]);
        }}
        title={
          viewJob
            ? `Dataset · ${jobSetLabel(viewJob)}`
            : "Dataset"
        }
        width={920}
      >
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div className="min-w-0 flex-1">
            <p className="text-xs text-[#6B7280]">
              {viewJob?.originalFileName || "—"}
              {viewJob?.totals?.success != null
                ? ` · ${viewJob.totals.success} saved`
                : ""}
              {viewJob?.totals?.fail
                ? ` · ${viewJob.totals.fail} failed`
                : ""}
              {viewMeta?.total != null ? ` · ${viewMeta.total} in set` : ""}
            </p>
            {String(viewJob?.status) === "partial" ? (
              <p className="mt-1 text-xs text-[#B45309]">
                Partial means successful rows are already saved. Use{" "}
                <span className="font-semibold">Publish to leads</span> so they
                appear in All Leads and WhatsApp bulk upload sets. Failed rows
                stay skipped (duplicate / invalid).
              </p>
            ) : null}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {defaultType === "leads" &&
            Number(viewJob?.totals?.success || viewMeta?.total || 0) > 0 ? (
              <>
                <YhButton
                  variant="secondary"
                  size="sm"
                  onClick={() => openInAllLeads(viewJob)}
                >
                  Filter in All Leads
                </YhButton>
                <YhButton
                  variant="primary"
                  size="sm"
                  disabled={assignLoading}
                  onClick={() => assignEntireSet(viewJob)}
                >
                  {assignLoading ? "Loading…" : "Assign all to team / person"}
                </YhButton>
              </>
            ) : null}
            {["partial", "failed", "completed"].includes(
              String(viewJob?.status)
            ) && Number(viewJob?.totals?.success || 0) > 0 ? (
              <YhButton
                variant="primary"
                size="sm"
                disabled={publishingId === String(viewJob?._id)}
                onClick={() => publishSet(viewJob)}
              >
                {publishingId === String(viewJob?._id)
                  ? "Publishing…"
                  : viewJob?.marketingReady
                  ? "Re-publish to leads"
                  : "Publish to leads"}
              </YhButton>
            ) : null}
            {["partial", "failed"].includes(String(viewJob?.status)) &&
            Number(viewJob?.totals?.fail || 0) > 0 ? (
              <YhButton
                variant="secondary"
                size="sm"
                onClick={() =>
                  downloadFailedReport(String(viewJob._id)).catch((err) =>
                    openErrorNotification(
                      err?.message || "Failed report not available"
                    )
                  )
                }
              >
                Download Failed.xlsx
              </YhButton>
            ) : null}
            <div className="min-w-[220px] sm:max-w-[280px]">
              <YhInput
                placeholder="Search name or phone…"
                value={viewSearch}
                onChange={(e: any) => {
                  setViewSearch(e.target.value);
                  setViewPage(0);
                }}
              />
            </div>
          </div>
        </div>
        <div className="relative min-h-[320px] overflow-hidden rounded-xl border border-[#F3F4F6]">
          <YhTable
            columns={viewColumns}
            rows={viewRows}
            getRowId={(row) => String(row._id)}
            loading={viewLoading}
            emptyMessage="No rows in this dataset"
            className="min-h-[320px]"
            page={viewPage}
            pageSize={viewPageSize}
            totalRows={viewMeta?.total || 0}
            paginationLabel="rows"
            onPageChange={setViewPage}
            onPageSizeChange={(s) => {
              setViewPageSize(s);
              setViewPage(0);
            }}
          />
        </div>
      </YhModal>

      <BulkAssignModal
        open={assignOpen}
        onClose={() => {
          setAssignOpen(false);
          setAssignLeadIds([]);
          setAssignJob(null);
        }}
        onAssigned={() => {
          setAssignOpen(false);
          setAssignLeadIds([]);
          openSuccessNotification(
            `Assigned ${assignLeadIds.length} lead(s) from dataset`
          );
          if (viewJob?._id) {
            loadViewRecords(
              String(viewJob._id),
              viewPage,
              viewPageSize,
              viewSearch
            );
          }
        }}
        leadIds={assignLeadIds}
        sourceHint={assignJob ? jobSetLabel(assignJob) : ""}
        defaultOrganizationId={
          String(
            assignJob?.organizationId ||
              (Array.isArray(assignJob?.organizationIds)
                ? assignJob.organizationIds[0]
                : "") ||
              activeOrganizationId ||
              ""
          )
        }
      />
    </div>
  );
};

export default BulkUploadSetsPage;
